/**
 * Menu API hooks for HMH Lunch Ordering System.
 */

import { useState, useEffect, useCallback } from "react";
import { apiConfig } from "../config/msalConfig";

// Types
export interface ItemOptionsPayload {
  prompt: string | null;
  options: { id: number; name: string }[];
}

export interface MenuItem {
  id?: number;
  name: string;
  description: string | null;
  item_options?: ItemOptionsPayload | null;
}

export interface DailySpecial {
  id?: number;
  name: string;
  description: string | null;
  replaces_regular: boolean;
  replacement_mode?: 'add' | 'replace_special' | 'replace_lunch';
  sort_order?: number;
  item_options?: ItemOptionsPayload | null;
}

export interface TodaysMenu {
  date: string;
  day_name: string;
  week_number: number;
  regular_lunch: MenuItem | null;
  daily_special: DailySpecial | null;
  daily_specials?: DailySpecial[];
  show_regular_lunch?: boolean;
  salad_available: boolean;
}

export interface WeekDay {
  day_of_week: number;
  day_name: string;
  menu_item: string;
  description: string | null;
}

export interface WeekMenu {
  week_number: number;
  days: WeekDay[];
}

export interface ToppingItem {
  id: number;
  name: string;
}

export interface ToppingCategory {
  name: string;
  toppings: ToppingItem[];
}

export interface SaladToppings {
  categories: ToppingCategory[];
}

// Hook for todays menu - uses /menu/today-for-ordering which handles cutoff logic
export function useTodaysMenu() {
  const [menu, setMenu] = useState<TodaysMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the ordering endpoint which handles cutoff logic automatically
      // Add cache-busting timestamp to prevent browser caching stale menu data
      const url = `${apiConfig.baseUrl}/menu/today-for-ordering?_t=${Date.now()}`;

      const response = await fetch(url, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch menu: ${response.statusText}`);
      }

      const data = await response.json();
      // The endpoint returns menu data with ordering_status
      setMenu(data);
    } catch (err) {
      console.error("Error fetching todays menu:", err);
      setError(err instanceof Error ? err.message : "Failed to load menu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  return { menu, loading, error, refetch: fetchMenu };
}

// Hook for week menu
export function useWeekMenu(weekNumber?: number) {
  const [menu, setMenu] = useState<WeekMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const url = weekNumber
        ? `${apiConfig.baseUrl}/menu/week?week_number=${weekNumber}`
        : `${apiConfig.baseUrl}/menu/week`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch week menu: ${response.statusText}`);
      }

      const data = await response.json();
      setMenu(data);
    } catch (err) {
      console.error("Error fetching week menu:", err);
      setError(err instanceof Error ? err.message : "Failed to load week menu");
    } finally {
      setLoading(false);
    }
  }, [weekNumber]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  return { menu, loading, error, refetch: fetchMenu };
}

// Hook for salad toppings
export function useSaladToppings() {
  const [toppings, setToppings] = useState<SaladToppings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchToppings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiConfig.baseUrl}/menu/salad-toppings`);

      if (!response.ok) {
        throw new Error(`Failed to fetch salad toppings: ${response.statusText}`);
      }

      const data = await response.json();
      setToppings(data);
    } catch (err) {
      console.error("Error fetching salad toppings:", err);
      setError(err instanceof Error ? err.message : "Failed to load salad toppings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToppings();
  }, [fetchToppings]);

  return { toppings, loading, error, refetch: fetchToppings };
}

// Calendar Types
export interface CalendarDay {
  date: string;
  day_name: string;
  is_today: boolean;
  regular_lunch: MenuItem | null;
  daily_special: DailySpecial | null;
  daily_specials?: DailySpecial[];
}

export interface CalendarWeek {
  week_start: string;
  week_number: number;
  days: CalendarDay[];
}

export interface MenuCalendar {
  start_date: string;
  weeks_count: number;
  weeks: CalendarWeek[];
}

// Hook for menu calendar
export function useMenuCalendar(weeks: number = 4) {
  const [calendar, setCalendar] = useState<MenuCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCalendar = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiConfig.baseUrl}/menu/calendar?weeks=${weeks}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch menu calendar: ${response.statusText}`);
      }

      const data = await response.json();
      setCalendar(data);
    } catch (err) {
      console.error("Error fetching menu calendar:", err);
      setError(err instanceof Error ? err.message : "Failed to load menu calendar");
    } finally {
      setLoading(false);
    }
  }, [weeks]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  return { calendar, loading, error, refetch: fetchCalendar };
}
